/**
 * /classes — list of classes for the i5 MVP. Backed by GET
 * /api/school/classes (FastAPI school-backend).
 *
 * Admin view: lists every class + can create new ones (teacher
 * assignment in the same form). Teacher view: lists only `mine=true`.
 */
import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";

import { useAuth } from "@/auth/OidcProvider";
import { canManageSchool, useClasses, useEligibleTeachers, useOrgMe } from "../../api/school";
import { CreateClassModal } from "./CreateClassModal";

export function ClassesPage() {
  const { t } = useTranslation();
  const { state } = useAuth();
  const isAdmin = state.status === "authenticated" && state.user.isAdmin;
  const orgMe = useOrgMe();
  // Group admin or the school's principal can manage classes (org P1).
  const canManage = isAdmin || canManageSchool(orgMe.data);
  // Teachers see only their own classes; admins see everything; a
  // principal sees their whole school.
  const { data, isLoading, error } = useClasses(
    isAdmin
      ? undefined
      : canManage
        ? { school_id: orgMe.data?.school_id ?? undefined }
        : { mine: true },
  );
  // Admin pulls the eligible-teachers list to resolve teacher_id → name on
  // each card. Teacher view doesn't have admin permission for this endpoint
  // and doesn't need it (they're the only teacher whose card they care about).
  const eligibleTeachers = useEligibleTeachers(canManage);
  const teacherNameById = useMemo(() => {
    const m = new Map<string, string>();
    for (const t of eligibleTeachers.data ?? []) {
      if (t.teacher_id) m.set(t.teacher_id, t.display_name || t.email);
    }
    return m;
  }, [eligibleTeachers.data]);
  const [showCreate, setShowCreate] = useState(false);

  return (
    <div className="p-6 max-w-5xl mx-auto">
      <header className="mb-6 flex items-baseline justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-slate-800">
            {canManage ? t("classes.title") : t("classes.myTitle")}
          </h1>
          <p className="text-xs text-slate-500 mt-1">
            {canManage ? t("classes.titleHintAdmin") : t("classes.titleHintTeacher")}
          </p>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-sm text-slate-500">{t("classes.semester")}</span>
          {canManage && (
            <button
              type="button"
              onClick={() => setShowCreate(true)}
              className="px-3 py-1.5 text-sm font-medium rounded bg-sky-500 text-white hover:bg-sky-600"
            >
              {t("classes.newClass")}
            </button>
          )}
        </div>
      </header>

      {isLoading && <p className="text-slate-500 italic">{t("common.loading")}</p>}
      {error && (
        <div className="rounded border border-red-200 bg-red-50 p-4 text-sm text-red-700">
          {(error as Error).message}
        </div>
      )}

      {!isLoading && !error && (data?.length ?? 0) === 0 && (
        <div className="rounded border border-slate-200 bg-white p-8 text-center text-slate-500">
          {isAdmin ? t("classes.emptyAdmin") : t("classes.emptyTeacher")}
        </div>
      )}

      <ul className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {data?.map((klass) => {
          // #38:班级只有行政班,卡片不再显示学科(学科在班级详情的「课程」里)。
          return (
            <li key={klass.id}>
              <Link
                to={`/classes/${klass.id}`}
                className="block rounded-lg border border-slate-200 bg-white p-5 hover:border-sky-300 hover:shadow-sm transition"
              >
                <div className="flex items-baseline justify-between mb-2">
                  <h2 className="text-lg font-medium text-slate-800">{klass.name}</h2>
                </div>
                <div className="text-sm text-slate-600 flex gap-4">
                  <span>
                    {t("common.grade")} <span className="font-medium text-slate-800">{klass.grade}</span>
                  </span>
                  {klass.section && (
                    <span>
                      {t("common.section")} <span className="font-medium text-slate-800">{klass.section}</span>
                    </span>
                  )}
                  <span className="text-xs text-slate-400 ml-auto">{klass.semester}</span>
                </div>
                {/* Teacher row — only shown when we can resolve names
                    (admin view loads /admin/teachers/eligible). For
                    teachers viewing their own classes the data isn't
                    fetched (admin endpoint), so we render nothing
                    rather than a confusing "0 teachers" badge. */}
                {isAdmin && klass.teacher_ids.length > 0 && (
                  <div className="mt-2 text-xs text-slate-500 truncate">
                    {t("classes.teachersLabel")}{" "}
                    <span className="text-slate-700">
                      {klass.teacher_ids
                        .map((id) => teacherNameById.get(id) ?? id.slice(0, 8))
                        .join(", ")}
                    </span>
                  </div>
                )}
                {isAdmin && klass.teacher_ids.length === 0 && (
                  <div className="mt-2 text-xs text-amber-700">
                    {t("classes.noTeacherAssigned")}
                  </div>
                )}
              </Link>
            </li>
          );
        })}
      </ul>

      {showCreate && (
        <CreateClassModal onClose={() => setShowCreate(false)} />
      )}
    </div>
  );
}
